<template>
  <footer class="footer">
    <div class="footer-inner">
      <div class="footer-grid">
        <!-- 产品 -->
        <div class="footer-col">
          <h3 class="footer-col-title">{{ $t('footer.product') }}</h3>
          <div class="footer-group">
            <button
              type="button"
              class="footer-group-toggle"
              :class="{ open: isGroupOpen('video') }"
              :aria-expanded="isGroupOpen('video') ? 'true' : 'false'"
              @click="toggleGroup('video', true)"
            >
              <span>{{ $t('footer.videoModels') }}</span>
              <svg class="footer-group-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div v-show="isGroupOpen('video')" class="footer-group-links">
              <router-link
                v-for="m in videoModels"
                :key="'fv-' + m.id"
                :to="langPath(getVideoModelPath(m))"
              >{{ getModelName(m.id, 'video') }}</router-link>
            </div>
          </div>
          <div class="footer-group">
            <button
              type="button"
              class="footer-group-toggle"
              :class="{ open: isGroupOpen('image') }"
              :aria-expanded="isGroupOpen('image') ? 'true' : 'false'"
              @click="toggleGroup('image', true)"
            >
              <span>{{ $t('footer.imageModels') }}</span>
              <svg class="footer-group-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div v-show="isGroupOpen('image')" class="footer-group-links">
              <router-link
                v-for="m in imageModels"
                :key="'fi-' + m.id"
                :to="langPath(getImageModelPath(m))"
              >{{ getModelName(m.id, 'image') }}</router-link>
            </div>
          </div>
          <div class="footer-group">
            <button
              type="button"
              class="footer-group-toggle"
              :class="{ open: isGroupOpen('music') }"
              :aria-expanded="isGroupOpen('music') ? 'true' : 'false'"
              @click="toggleGroup('music', true)"
            >
              <span>{{ $t('footer.musicModels') }}</span>
              <svg class="footer-group-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div v-show="isGroupOpen('music')" class="footer-group-links">
              <router-link :to="navTo('Music')">{{ $t('footer.musicGenerator') }}</router-link>
            </div>
          </div>
        </div>

        <!-- 应用 -->
        <div class="footer-col">
          <h3 class="footer-col-title">{{ $t('footer.apps') }}</h3>
          <nav class="footer-links">
            <router-link
              v-for="app in footerApps"
              :key="'fa-' + app.key"
              :to="langPath('/apps/' + app.key)"
            >{{ app.name }}</router-link>
          </nav>
        </div>

        <!-- 工具 -->
        <div class="footer-col">
          <h3 class="footer-col-title">{{ $t('footer.tools') }}</h3>
          <nav class="footer-links">
            <router-link
              v-for="t in footerTools"
              :key="'ft-' + t.id"
              :to="toolPath(t)"
            >{{ getToolName(t.id) }}</router-link>
          </nav>
        </div>

        <!-- 公司 -->
        <div class="footer-col">
          <h3 class="footer-col-title">{{ $t('footer.company') }}</h3>
          <nav class="footer-links">
            <router-link :to="navTo('About')">{{ $t('footer.about') }}</router-link>
            <router-link :to="navTo('Contact')">{{ $t('footer.contact') }}</router-link>
            <router-link :to="navTo('Pricing')">{{ $t('footer.pricing') }}</router-link>
            <div class="footer-group">
              <div class="footer-group-head">
                <router-link class="footer-group-link" :to="navTo('Hub')">{{ $t('footer.blog') }}</router-link>
                <button
                  type="button"
                  class="footer-group-icon-btn"
                  :class="{ open: isGroupOpen('blog') }"
                  :aria-expanded="isGroupOpen('blog') ? 'true' : 'false'"
                  :aria-label="$t('footer.blog')"
                  @click="toggleGroup('blog', false)"
                >
                  <svg class="footer-group-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
              </div>
              <div v-show="isGroupOpen('blog')" class="footer-group-links">
                <router-link
                  v-for="cat in blogCategories"
                  :key="'fc-' + cat.key"
                  :to="langPath('/hub/' + cat.key)"
                >{{ cat.label }}</router-link>
              </div>
            </div>
            <router-link :to="navTo('Privacy')">{{ $t('footer.privacy') }}</router-link>
          </nav>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
var hubConfig = require('@/config/hub')
var appConfig = require('@/config/appConfig')
var getImageModelPath = require('@/config/imageModelSeoPaths').getImageModelPath

var FOOTER_APP_KEYS = [
  'viral-video',
  'celebrity-video',
  'product-showcase',
  'tiktok-ads',
  'ai-avatar',
  'music-video',
  'movie-trailer',
  'home-modern-kitchen'
]

var FOOTER_TOOL_IDS = [
  'watermark-remover',
  'handwritten-erase',
  'doc-crop-enhance',
  'object-remover',
  'face-swap',
  'background-remover',
  'ai-text-remover',
  'ai-change-clothes',
  'photo-restore'
]

var BLOG_CATEGORY_KEYS = [
  'ai-model-insights',
  'reviews',
  'how-to-guides',
  'alternatives',
  'prompt-guides'
]

var PRODUCT_GROUP_KEYS = ['video', 'image', 'music']

export default {
  name: 'AppFooter',
  data: function () {
    return {
      openGroups: {}
    }
  },
  computed: {
    imageModels: function () {
      return this.$store.state.imageModels || []
    },
    videoModels: function () {
      return this.$store.state.videoModels || []
    },
    footerApps: function () {
      var map = appConfig.getAppMap(this.$t.bind(this))
      var byKey = {}
      var key
      var i
      var list
      for (key in map) {
        if (!Object.prototype.hasOwnProperty.call(map, key)) continue
        list = map[key] || []
        for (i = 0; i < list.length; i++) {
          byKey[list[i].key] = list[i]
        }
      }
      var out = []
      for (i = 0; i < FOOTER_APP_KEYS.length; i++) {
        if (byKey[FOOTER_APP_KEYS[i]]) out.push(byKey[FOOTER_APP_KEYS[i]])
      }
      return out
    },
    footerTools: function () {
      var tools = this.$store.state.tools || []
      var byId = {}
      var i
      for (i = 0; i < tools.length; i++) {
        byId[tools[i].id] = tools[i]
      }
      var out = []
      for (i = 0; i < FOOTER_TOOL_IDS.length; i++) {
        if (byId[FOOTER_TOOL_IDS[i]]) out.push(byId[FOOTER_TOOL_IDS[i]])
      }
      return out
    },
    blogCategories: function () {
      var cats = hubConfig.getHubCategories(this.$i18n.locale) || []
      var byKey = {}
      var i
      for (i = 0; i < cats.length; i++) {
        byKey[cats[i].key] = cats[i]
      }
      var out = []
      for (i = 0; i < BLOG_CATEGORY_KEYS.length; i++) {
        if (byKey[BLOG_CATEGORY_KEYS[i]]) out.push(byKey[BLOG_CATEGORY_KEYS[i]])
      }
      return out
    }
  },
  methods: {
    isGroupOpen: function (key) {
      return !!this.openGroups[key]
    },
    toggleGroup: function (key, exclusive) {
      var next = !this.openGroups[key]
      if (exclusive && next) {
        var i
        for (i = 0; i < PRODUCT_GROUP_KEYS.length; i++) {
          this.$set(this.openGroups, PRODUCT_GROUP_KEYS[i], PRODUCT_GROUP_KEYS[i] === key)
        }
        return
      }
      this.$set(this.openGroups, key, next)
    },
    langPath: function (path) {
      var lang = this.$route.params.lang
      return lang ? '/' + lang + path : path
    },
    navTo: function (name) {
      var lang = this.$route.params.lang
      return lang ? { name: name, params: { lang: lang } } : { name: name }
    },
    toolPath: function (tool) {
      var lang = this.$route.params.lang
      var base = lang ? '/' + lang : ''
      if (tool.category === 'document') {
        return base + '/tools/doc-convert/' + tool.id
      }
      return base + '/tools/' + tool.id
    },
    getImageModelPath: function (model) {
      return getImageModelPath(model)
    },
    // 视频模型已有独立 SEO 页面时优先进入对应页面，其余保持生成页链接。
    getVideoModelPath: function (model) {
      if (model && model.id === 'veo-3-1') return '/video-models/veo-3-1-ai-video-generator'
      if (model && model.id === 'sora-2') return '/video-models/sora-2-ai-video-generator'
      return '/models/' + (model.routeId || model.id)
    },
    getModelName: function (id, type) {
      if (id === 'kling-3') return 'Kling 3'
      var path = type === 'video' ? 'models.video.' + id : 'models.image.' + id
      var model = this.$t(path)
      if (model && typeof model === 'object' && model.name) return model.name
      var list = type === 'video' ? this.videoModels : this.imageModels
      var i
      for (i = 0; i < list.length; i++) {
        if (list[i].id === id) return list[i].navName || list[i].name || id
      }
      return id
    },
    getToolName: function (id) {
      var tools = this.$t('tools')
      var tool = tools && typeof tools === 'object' ? tools[id] : null
      var name = tool && tool.name ? String(tool.name) : id
      return name
        .replace(/^\s*免费\s*/, '')
        .replace(/^\s*Free\s+/i, '')
        .trim()
    }
  }
}
</script>

<style scoped>
.footer {
  background: linear-gradient(180deg, #fafafa 0%, #f3f4f6 100%);
  border-top: 1px solid #e8eaed;
  padding: 56px 32px 48px;
  margin-top: auto;
  overflow-x: hidden;
}
.footer-inner {
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
}
.footer-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 40px 48px;
  width: 100%;
  align-items: start;
}
.footer-col {
  min-width: 0;
}
.footer-col-title {
  margin: 0 0 16px;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0.01em;
  color: #111827;
}
.footer-links {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.footer-col a,
.footer-group-toggle {
  font-size: 13px;
  line-height: 1.4;
  color: #4b5563;
  transition: color 0.18s ease;
}
.footer-col a {
  display: block;
  padding: 5px 0;
  text-decoration: none;
  border-radius: 4px;
}
.footer-col a:hover {
  color: #059669;
}
.footer-group {
  margin: 0;
}
.footer-group-toggle {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  max-width: 100%;
  margin: 0;
  padding: 5px 0;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 400;
  text-align: left;
}
.footer-group-toggle:hover,
.footer-group-toggle.open {
  color: #059669;
}
.footer-group-head {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  max-width: 100%;
}
.footer-group-link {
  padding: 5px 0 !important;
}
.footer-group-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: none;
  color: #6b7280;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.18s ease, background 0.18s ease;
}
.footer-group-icon-btn:hover,
.footer-group-icon-btn.open {
  color: #059669;
  background: rgba(5, 150, 105, 0.06);
}
.footer-group-chevron {
  flex-shrink: 0;
  opacity: 0.75;
  transition: transform 0.2s ease, opacity 0.18s ease;
}
.footer-group-toggle:hover .footer-group-chevron,
.footer-group-toggle.open .footer-group-chevron,
.footer-group-icon-btn:hover .footer-group-chevron,
.footer-group-icon-btn.open .footer-group-chevron {
  opacity: 1;
}
.footer-group-toggle.open .footer-group-chevron,
.footer-group-icon-btn.open .footer-group-chevron {
  transform: rotate(180deg);
}
.footer-group-links {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 2px 0 6px 12px;
  margin: 0 0 2px;
  border-left: 1px solid #e5e7eb;
}
.footer-group-links a {
  padding: 4px 0;
  font-size: 12.5px;
  color: #6b7280;
}
.footer-group-links a:hover {
  color: #059669;
}

@media (max-width: 900px) {
  .footer {
    padding: 48px 24px 40px;
  }
  .footer-inner {
    max-width: 520px;
  }
  .footer-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 36px 40px;
  }
}
@media (max-width: 520px) {
  .footer {
    padding: 40px 16px 32px;
  }
  .footer-inner {
    max-width: 100%;
  }
  .footer-grid {
    gap: 32px 28px;
  }
  .footer-col-title {
    margin-bottom: 12px;
  }
}
</style>
